import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { ArrowLeft, DollarSign, TrendingUp, Clock, MapPin, Truck, CheckCircle2, FileDown, FileText, Loader2, Landmark, Zap } from "lucide-react";
import { CATEGORY_LABELS } from "@/lib/constants";
import jsPDF from "jspdf";
import { toast } from "sonner";

const MONTHS = ["Alle måneder", "Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];

export default function Earnings() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const fetchData = () => {
    const params = {};
    if (year !== "all") params.year = year;
    if (month) params.month = month;
    api.get("/earnings/me", { params }).then(r => setData(r.data));
  };

  useEffect(fetchData, [year, month]);

  if (!data) return <div className="p-8 text-center text-muted-foreground">Indlæser...</div>;

  const paid = data.items.filter(p => p.status === "paid");
  const pending = data.items.filter(p => p.status === "pending");

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (year !== "all") params.set("year", year);
      if (month) params.set("month", month);
      const token = localStorage.getItem("grabber_token");
      const res = await fetch(`${API_BASE}/earnings/me/export.csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `grabber-indtaegter-${year}${month ? `-${String(month).padStart(2, "0")}` : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloadet");
    } catch (e) { toast.error("CSV fejlede"); }
    finally { setDownloading(false); }
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    let y = 18;
    doc.setFillColor(56, 109, 84);
    doc.rect(0, 0, W, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Grabber", 15, 8);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Indtægtsrapport", W - 15, 8, { align: "right" });

    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    y = 25;
    doc.text("Mine indtægter", 15, y);

    y += 7;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(110, 110, 110);
    const period = year === "all" ? "Alle år" : `${MONTHS[month] || "Hele"} ${year}`;
    doc.text(`Periode: ${period}`, 15, y);
    doc.text(`Udstedt: ${new Date().toLocaleDateString("da-DK")}`, W - 15, y, { align: "right" });

    y += 10;
    doc.setFillColor(248, 247, 244);
    doc.roundedRect(15, y, W - 30, 22, 2, 2, "F");
    doc.setTextColor(20, 20, 20);
    doc.setFontSize(9);
    y += 7;
    doc.text("Total udbetalt:", 19, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(56, 109, 84);
    doc.text(`${data.total_paid.toLocaleString("da-DK")} kr.`, W - 19, y, { align: "right" });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(20, 20, 20);
    doc.text("Afventer udbetaling:", 19, y);
    doc.setFont("helvetica", "bold");
    doc.text(`${data.total_pending.toLocaleString("da-DK")} kr.`, W - 19, y, { align: "right" });

    y += 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Detaljer", 15, y);
    y += 6;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text("Dato", 15, y);
    doc.text("Titel", 40, y);
    doc.text("Status", 130, y);
    doc.text("Beløb", W - 15, y, { align: "right" });
    y += 2;
    doc.setDrawColor(220, 220, 220);
    doc.line(15, y, W - 15, y);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(20, 20, 20);
    data.items.forEach((it) => {
      y += 6;
      if (y > 280) { doc.addPage(); y = 20; }
      doc.text((it.created_at || "").slice(0, 10), 15, y);
      const title = (it.job_title || `Job #${it.job_id.slice(0, 8)}`).slice(0, 38);
      doc.text(title, 40, y);
      doc.setTextColor(it.status === "paid" ? 0x38 : 0xb0, it.status === "paid" ? 0x6d : 0x70, it.status === "paid" ? 0x54 : 0x40);
      doc.text(it.status === "paid" ? "Udbetalt" : "Afventer", 130, y);
      doc.setTextColor(20, 20, 20);
      doc.setFont("helvetica", "bold");
      doc.text(`${it.amount.toFixed(2).replace(".", ",")} kr.`, W - 15, y, { align: "right" });
      doc.setFont("helvetica", "normal");
    });

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("Grabber.dk · Indtægter er momsfri privatperson-indkomst, check med SKAT for din situation", W / 2, 290, { align: "center" });
    doc.save(`grabber-indtaegter-${year}${month ? "-" + String(month).padStart(2, "0") : ""}.pdf`);
    toast.success("PDF downloadet");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 relative">
      <div className="absolute top-0 right-0 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl -z-10 pointer-events-none animate-float" />

      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage til dashboard
      </Link>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Mine indtægter</h1>
        <p className="text-muted-foreground">Historik over alle dine udbetalinger som chauffør</p>
      </motion.div>

      <ConnectCard />

      {/* Filters + Export */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32" data-testid="filter-year"><SelectValue placeholder="År" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle år</SelectItem>
                {(data.available_years || []).map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))} disabled={year === "all"}>
              <SelectTrigger className="w-40" data-testid="filter-month"><SelectValue placeholder="Måned" /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV} disabled={downloading || data.items.length === 0} data-testid="export-csv-btn" className="gap-1.5">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} CSV
            </Button>
            <Button variant="outline" size="sm" onClick={downloadPDF} disabled={data.items.length === 0} data-testid="export-pdf-btn" className="gap-1.5">
              <FileText className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary cards */}
      <motion.div
        initial="hidden" animate="show"
        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
        className="grid sm:grid-cols-3 gap-4 mb-8"
      >
        <SummaryCard label="Total udbetalt" value={`${data.total_paid.toLocaleString("da-DK")} kr.`} sub={`${data.count_paid} udbetalinger`} icon={<DollarSign />} color="bg-emerald-50 text-emerald-700" />
        <SummaryCard label="Afventer udbetaling" value={`${data.total_pending.toLocaleString("da-DK")} kr.`} sub={`${data.count_pending} afventer`} icon={<Clock />} color="bg-amber-50 text-amber-700" />
        <SummaryCard label="Total optjent" value={`${(data.total_paid + data.total_pending).toLocaleString("da-DK")} kr.`} sub={`${data.items.length} jobs`} icon={<TrendingUp />} color="bg-primary/10 text-primary" />
      </motion.div>

      {data.items.length === 0 ? (
        <Card className="p-12 text-center">
          <Truck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">Ingen indtægter i denne periode</h3>
          <p className="text-muted-foreground mb-4">Når du fuldfører jobs og kunden godkender, vises dine udbetalinger her.</p>
        </Card>
      ) : (
        <Tabs defaultValue="all">
          <TabsList className="mb-6">
            <TabsTrigger value="all" data-testid="tab-all-earnings">Alle ({data.items.length})</TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid">Udbetalt ({paid.length})</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Afventer ({pending.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="all"><EarningsList items={data.items} /></TabsContent>
          <TabsContent value="paid"><EarningsList items={paid} /></TabsContent>
          <TabsContent value="pending"><EarningsList items={pending} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/connect/status").then(r => setStatus(r.data)).catch(() => {});
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect") === "return") toast.success("Stripe-opsætning gennemført, vi tjekker status...");
  }, []);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/connect/onboarding", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke starte onboarding");
      setLoading(false);
    }
  };

  if (!status) return null;

  if (status.payouts_enabled) {
    return (
      <Card className="p-4 mb-6 border-emerald-200 bg-emerald-50/50" data-testid="connect-card-active">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><Zap className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="font-semibold text-sm flex items-center gap-1.5">Automatiske udbetalinger aktive <CheckCircle2 className="w-4 h-4 text-emerald-600" /></p>
            <p className="text-xs text-muted-foreground">Din Stripe-konto er tilkoblet. Du modtager 80% automatisk, når kunden godkender et job.</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 mb-6 border-amber-200 bg-amber-50/50" data-testid="connect-card-setup">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Landmark className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm">
            {status.details_submitted ? "Stripe gennemgår dine oplysninger" : "Få dine penge automatisk"}
          </p>
          <p className="text-xs text-muted-foreground">
            {status.details_submitted
              ? "Næsten klar, Stripe verificerer din konto. Det tager typisk få minutter."
              : "Tilkobl din bankkonto via Stripe, så udbetales 80% automatisk ved hvert godkendt job, ingen ventetid."}
          </p>
        </div>
        <Button size="sm" onClick={start} disabled={loading} data-testid="connect-onboarding-btn" className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />}
          {status.details_submitted ? "Fortsæt opsætning" : "Opret udbetalingskonto"}
        </Button>
      </div>
    </Card>
  );
}

function SummaryCard({ label, value, sub, icon, color }) {
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}>
      <Card className="p-5 lift">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color} [&>svg]:w-5 [&>svg]:h-5`}>{icon}</div>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub && <p className="text-[11px] text-muted-foreground/70 mt-1">{sub}</p>}
      </Card>
    </motion.div>
  );
}

function EarningsList({ items }) {
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Ingen i denne kategori</Card>;
  return (
    <div className="space-y-2">
      {items.map(item => <EarningsRow key={item.id} item={item} />)}
    </div>
  );
}

function EarningsRow({ item }) {
  const isPaid = item.status === "paid";
  return (
    <Link to={`/jobs/${item.job_id}`} data-testid={`earning-row-${item.id}`}>
      <Card className="p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant={isPaid ? "default" : "outline"} className="text-xs gap-1">
                {isPaid && <CheckCircle2 className="w-3 h-3" />}
                {isPaid ? "Udbetalt" : "Afventer"}
              </Badge>
              {item.method === "stripe_connect" && <Badge variant="secondary" className="text-xs gap-1"><Zap className="w-3 h-3" /> Automatisk</Badge>}
              {item.job_category && <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[item.job_category]}</Badge>}
            </div>
            <p className="font-semibold truncate">{item.job_title || `Job #${item.job_id.slice(0,8)}`}</p>
            {item.pickup_address && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {item.pickup_address}</p>
            )}
            {item.customer_name && <p className="text-xs text-muted-foreground mt-0.5">For {item.customer_name}</p>}
            <p className="text-[11px] text-muted-foreground/70 mt-1.5">
              {isPaid && item.paid_at ? `Udbetalt ${new Date(item.paid_at).toLocaleDateString("da-DK")}` : `Oprettet ${new Date(item.created_at).toLocaleDateString("da-DK")}`}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-bold text-primary">+{item.amount} kr.</p>
            <p className="text-[11px] text-muted-foreground">80% af job</p>
          </div>
        </div>
      </Card>
    </Link>
  );
}
